"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";
import type { VisualCmsSection, VisualCmsSectionType } from "@/types/visual-cms";
import { HeroSection } from "./HeroSection";
import { FaqSection } from "./FaqSection";
import { RichTextSection } from "./RichTextSection";
import { CardGridSection } from "./CardGridSection";
import { CtaBannerSection } from "./CtaBannerSection";
import { TrustBlocksSection } from "./TrustBlocksSection";
import { TimelineSection } from "./TimelineSection";
import { StepsSection } from "./StepsSection";
import { StatsSection } from "./StatsSection";
import { FeatureGridSection } from "./FeatureGridSection";
import { ImageTextSection } from "./ImageTextSection";
import { GallerySection } from "./GallerySection";
import { PlaceholderSection } from "./PlaceholderSection";

interface PageRendererProps {
  sections: VisualCmsSection[];
  /** When true, hide inactive sections (mirrors the public site contract). */
  hideInactive?: boolean;
}

function pickRenderer(type: VisualCmsSectionType) {
  switch (type) {
    case "hero":
      return HeroSection;
    case "faq":
      return FaqSection;
    case "rich_text":
      return RichTextSection;
    case "card_grid":
      return CardGridSection;
    case "feature_grid":
      return FeatureGridSection;
    case "trust_blocks":
      return TrustBlocksSection;
    case "timeline":
      return TimelineSection;
    case "steps":
      return StepsSection;
    case "stats":
      return StatsSection;
    case "image_text":
      return ImageTextSection;
    case "gallery":
      return GallerySection;
    case "cta_banner":
      return CtaBannerSection;
    default:
      return PlaceholderSection;
  }
}

class SectionErrorBoundary extends Component<
  { sectionId: string; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`Visual CMS section ${this.props.sectionId} crashed:`, error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <section className="py-10">
          <div className="max-w-3xl mx-auto px-4">
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
              This section couldn't render — its content may be malformed. Check the editor for
              this section.
            </div>
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}

export function PageRenderer({ sections, hideInactive = false }: PageRendererProps) {
  const visible = hideInactive
    ? sections.filter((s) => s.is_active !== false)
    : sections;

  if (visible.length === 0) {
    return (
      <div className="py-20 text-center text-sm text-gray-500">
        No sections to display.
      </div>
    );
  }

  return (
    <>
      {visible.map((section) => {
        const Renderer = pickRenderer(section.section_type);
        return (
          <SectionErrorBoundary key={section.id} sectionId={section.id}>
            <Renderer section={section} />
          </SectionErrorBoundary>
        );
      })}
    </>
  );
}
